import React, { useEffect, useRef, useCallback, useState } from 'react';
import { OrderStatusBadge } from './OrderStatusBadge';
import type { OrderStatus } from './OrderStatusBadge';
import { useOrderDetailQuery } from '../../hooks/useSelfServiceBilling';
import { formatCents, formatBillingDate } from '../../utils/billing-formatters';
import type {
  SelfServiceOrderDetail,
  SelfServiceOrderDocument,
} from '../../services/selfServiceBillingApi';
import apiClient from '../../services/apiClient';
import { PRICING_TYPE_LABELS } from '../../types/billing-labels';

// =============================================================================
// LABEL MAPPINGS
// =============================================================================

const DOC_TYPE_LABELS: Record<string, string> = {
  PROFORMA: 'Proforma',
  INVOICE: 'Faktura VAT',
};

const DOC_STATUS_LABELS: Record<string, string> = {
  DRAFT: 'Szkic',
  ISSUED: 'Wystawiony',
  PAID: 'Opłacony',
  CANCELED: 'Anulowany',
};

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  BANK_TRANSFER: 'Przelew bankowy',
  STRIPE: 'Karta płatnicza',
};

const BILLING_INTERVAL_LABELS: Record<string, string> = {
  monthly: 'miesięczny',
  quarterly: 'kwartalny',
  yearly: 'roczny',
};

// =============================================================================
// PDF DOWNLOAD HELPER
// =============================================================================

/**
 * Extract filename from Content-Disposition header, fall back to provided default.
 */
const extractFilename = (headers: Record<string, unknown>, fallback: string): string => {
  const disposition = String(headers?.['content-disposition'] ?? '');
  const match = disposition.match(/filename="?([^";\n]+)"?/);
  return match?.[1] ?? fallback;
};

const handlePdfDownload = async (url: string, fallbackFilename: string) => {
  try {
    const response = await apiClient.get(url, { responseType: 'blob' });
    const filename = extractFilename(response.headers, fallbackFilename);
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(blobUrl);
  } catch {
    // Silently fail - user sees no download
  }
};

// =============================================================================
// ORDER DETAIL MODAL (AC-9)
// =============================================================================

interface OrderDetailModalProps {
  orderId: string;
  orderNumber: string;
  onClose: () => void;
  triggerRef: React.RefObject<HTMLTableRowElement | null>;
}

/**
 * Modal for order detail view.
 *
 * AC-9 requirements:
 * - role="dialog", aria-labelledby, aria-modal="true"
 * - Close via X button or Escape key
 * - Focus returns to trigger row on close
 * - Shows: order lines, documents (PDF proxy links), partySnapshot
 * - CANCELED: shows cancel reason section
 * - PDF link inactive when generating: spinner + "Trwa generowanie..."
 */
export const OrderDetailModal: React.FC<OrderDetailModalProps> = ({
  orderId,
  orderNumber,
  onClose,
  triggerRef,
}) => {
  const { data: order, isLoading, isError } = useOrderDetailQuery(orderId);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const [downloadingDocId, setDownloadingDocId] = useState<string | null>(null);
  const modalTitleId = `order-detail-title-${orderId}`;

  // Escape-to-close
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Focus close button on open
  useEffect(() => {
    if (!isLoading && closeButtonRef.current) {
      closeButtonRef.current.focus();
    }
  }, [isLoading]);

  // Return focus to trigger row on close
  useEffect(() => {
    const el = triggerRef.current;
    return () => {
      if (el) {
        el.focus();
      }
    };
  }, [triggerRef]);

  // Focus trap for Tab/Shift+Tab within modal
  const handleTabTrap = useCallback((e: React.KeyboardEvent) => {
    if (e.key !== 'Tab') return;
    const modal = e.currentTarget;
    const focusable = modal.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }, []);

  const renderDocumentLink = (doc: SelfServiceOrderDocument) => {
    if (doc.status === 'generating') {
      return (
        <span className="inline-flex items-center text-gray-400 text-xs">
          <svg
            className="animate-spin h-3 w-3 mr-1"
            fill="none"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          Trwa generowanie...
        </span>
      );
    }

    const isDownloading = downloadingDocId === doc.id;
    return (
      <button
        disabled={isDownloading}
        onClick={async () => {
          setDownloadingDocId(doc.id);
          try {
            await handlePdfDownload(doc.proxyPdfUrl, `${doc.documentNumber || 'document'}.pdf`);
          } finally {
            setDownloadingDocId(null);
          }
        }}
        className={`text-xs font-medium ${isDownloading ? 'text-gray-400 cursor-not-allowed' : 'text-primary-700 hover:text-primary-800'}`}
      >
        {isDownloading ? 'Pobieranie...' : 'Pobierz PDF'}
      </button>
    );
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        data-testid="order-detail-modal"
        role="dialog"
        aria-labelledby={modalTitleId}
        aria-modal="true"
        onKeyDown={handleTabTrap}
        className="bg-white rounded-lg max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
          <h3
            id={modalTitleId}
            className="text-lg font-semibold text-neutral-900"
          >
            Zamówienie {orderNumber}
          </h3>
          <button
            ref={closeButtonRef}
            onClick={onClose}
            aria-label="Zamknij"
            className="text-neutral-400 hover:text-neutral-600"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="p-8 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : isError ? (
          <div className="p-8 text-center text-red-600" role="alert">
            <p>Nie udało się załadować szczegółów zamówienia.</p>
          </div>
        ) : order ? (
          <OrderDetailContent order={order} renderDocumentLink={renderDocumentLink} />
        ) : null}
      </div>
    </div>
  );
};

/**
 * Order detail content rendered inside the modal.
 */
const OrderDetailContent: React.FC<{
  order: SelfServiceOrderDetail;
  renderDocumentLink: (doc: SelfServiceOrderDocument) => React.ReactNode;
}> = ({ order, renderDocumentLink }) => {
  return (
    <div className="p-6 space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-2 gap-4 bg-neutral-50 p-4 rounded-md">
        <div>
          <span className="text-xs font-medium text-neutral-500 uppercase">
            Status
          </span>
          <div className="mt-1">
            <OrderStatusBadge status={order.status as OrderStatus} />
          </div>
        </div>
        <div>
          <span className="text-xs font-medium text-neutral-500 uppercase">
            Data zamówienia
          </span>
          <p className="text-sm text-neutral-800 mt-0.5">
            {formatBillingDate(order.createdAt)}
          </p>
        </div>
        <div>
          <span className="text-xs font-medium text-neutral-500 uppercase">
            Kwota netto
          </span>
          <p className="text-sm font-medium text-neutral-800 mt-0.5">
            {formatCents(order.totalNetCents)}
          </p>
        </div>
        <div>
          <span className="text-xs font-medium text-neutral-500 uppercase">
            Kwota brutto
          </span>
          <p className="text-sm font-medium text-neutral-800 mt-0.5">
            {formatCents(order.totalGrossCents)}
          </p>
        </div>
        <div>
          <span className="text-xs font-medium text-neutral-500 uppercase">
            VAT
          </span>
          <p className="text-sm text-neutral-800 mt-0.5">{order.vatRate}%</p>
        </div>
        <div>
          <span className="text-xs font-medium text-neutral-500 uppercase">
            Metoda płatności
          </span>
          <p className="text-sm text-neutral-800 mt-0.5">
            {PAYMENT_METHOD_LABELS[order.paymentMethod] || order.paymentMethod || '\u2014'}
          </p>
        </div>
      </div>

      {/* Cancel reason (AC-9: CANCELED shows reason section) */}
      {order.status === 'CANCELED' && order.cancelReason && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <h4 className="text-sm font-semibold text-red-800 mb-1">
            Powód anulowania
          </h4>
          <p className="text-sm text-red-700">{order.cancelReason}</p>
        </div>
      )}

      {/* Order Lines */}
      {order.lines && order.lines.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-neutral-900 mb-3">
            Pozycje zamówienia
          </h4>
          <div className="space-y-2">
            {order.lines.map((line) => (
              <div
                key={line.id}
                className="bg-neutral-50 border border-neutral-200 rounded-md p-3"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm font-medium text-neutral-800">
                      {line.description}
                    </p>
                    {(line.periodStart || line.periodEnd) && (
                      <p className="text-xs text-neutral-500 mt-0.5">
                        Okres: {formatBillingDate(line.periodStart)} &mdash;{' '}
                        {formatBillingDate(line.periodEnd)}
                      </p>
                    )}
                    {line.billingInterval && (
                      <p className="text-xs text-neutral-500">
                        Interwał: {BILLING_INTERVAL_LABELS[line.billingInterval] || line.billingInterval}
                      </p>
                    )}
                  </div>
                  {line.pricing && (
                    <div className="text-right">
                      <p className="text-sm font-medium text-neutral-800">
                        {line.pricing.netCents != null
                          ? formatCents(line.pricing.netCents)
                          : line.pricing.grossCents != null
                            ? formatCents(line.pricing.grossCents)
                            : '\u2014'}
                      </p>
                      {line.pricing.grossCents != null &&
                        line.pricing.netCents != null &&
                        line.pricing.netCents !== line.pricing.grossCents && (
                          <p className="text-xs text-neutral-500">
                            brutto: {formatCents(line.pricing.grossCents)}
                          </p>
                        )}
                      <p className="text-xs text-neutral-400">
                        {PRICING_TYPE_LABELS[line.pricing.type as keyof typeof PRICING_TYPE_LABELS] || line.pricing.type}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Documents (AC-9: PDF proxy links) */}
      {order.documents && order.documents.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-neutral-900 mb-3">
            Dokumenty
          </h4>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase">
                    Typ
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase">
                    Numer
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase">
                    Status
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase">
                    Kwota brutto
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase">
                    Wystawiony
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-neutral-500 uppercase">
                    PDF
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200">
                {order.documents.map((doc) => (
                  <tr key={doc.id} className="hover:bg-neutral-50">
                    <td className="px-3 py-2 text-xs font-medium">
                      {DOC_TYPE_LABELS[doc.type] || doc.type}
                    </td>
                    <td className="px-3 py-2 text-xs font-mono">
                      {doc.documentNumber}
                    </td>
                    <td className="px-3 py-2 text-xs">{DOC_STATUS_LABELS[doc.status] || doc.status}</td>
                    <td className="px-3 py-2 text-xs">
                      {formatCents(doc.amountGrossCents)}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {formatBillingDate(doc.issuedAt)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {renderDocumentLink(doc)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Party Snapshot (AC-9: whitelisted fields) */}
      {order.partySnapshot && (
        <div>
          <h4 className="text-sm font-semibold text-neutral-900 mb-3">
            Dane firmowe (na moment zamówienia)
          </h4>
          <div className="bg-neutral-50 border border-neutral-200 rounded-md p-4 grid grid-cols-2 gap-3 text-sm">
            <div>
              <span className="text-xs font-medium text-neutral-500 uppercase">
                Firma
              </span>
              <p className="text-neutral-800 mt-0.5">
                {order.partySnapshot.companyName}
              </p>
            </div>
            <div>
              <span className="text-xs font-medium text-neutral-500 uppercase">
                NIP
              </span>
              <p className="text-neutral-800 font-mono mt-0.5">
                {order.partySnapshot.nip}
              </p>
            </div>
            <div>
              <span className="text-xs font-medium text-neutral-500 uppercase">
                Adres
              </span>
              <p className="text-neutral-800 mt-0.5">
                {order.partySnapshot.address.street},{' '}
                {order.partySnapshot.address.postalCode}{' '}
                {order.partySnapshot.address.city}
              </p>
            </div>
            {order.partySnapshot.billingEmail && (
              <div>
                <span className="text-xs font-medium text-neutral-500 uppercase">
                  Email do faktur
                </span>
                <p className="text-neutral-800 mt-0.5">
                  {order.partySnapshot.billingEmail}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
